import React, { useEffect, useState } from "react";
import Select from "react-select";
import PerformerDropdownIndicator from "./PerformerDropdownIndicator";
import { useGetUserCompanies } from "../../../../backend/query";
import { FULL_USER_COMPANIES_PAYLOAD } from "../../../../backend/license";

interface PerformerFiltersProps {
  onChange: (filters: any) => void;
  establishmentType: string;
  onEstablishmentTypeChange: (value: string) => void;
}

const ALL_OPTION = {
  value: "all",
  label: "ALL",
  company: null,
  associate_company: null,
  location: null,
};

const ESTABLISHMENT_TYPES = [
  { value: "", label: "Select Establishment Type" },
  { value: "Factory", label: "Factory" },
  { value: "Establishment", label: "Establishment" },
  { value: "CLRA", label: "CLRA" },
];

function PerformerFilters({
  onChange,
  establishmentType,
  onEstablishmentTypeChange,
}: PerformerFiltersProps) {
  const [company, setCompany] = useState<any>(ALL_OPTION);
  const [associateCompany, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const hasAutoSelected = React.useRef(false);

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  const { userCompanies, isFetching } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      const companyOptions = [
        ALL_OPTION,
        ...userCompanies.map(function (c: any) {
          return { value: c.id, label: c.name, company: c };
        }),
      ];
      setCompanies(companyOptions);
    }
  }, [userCompanies]);

  // Auto-select first company on load (only once)
  useEffect(() => {
    if (userCompanies && userCompanies.length > 0 && company.value === "all" && !hasAutoSelected.current) {
      const firstCompany = {
        value: userCompanies[0].id,
        label: userCompanies[0].name,
        company: userCompanies[0]
      };
      setCompany(firstCompany);
      hasAutoSelected.current = true;
    }
  }, [userCompanies]);

  useEffect(() => {
    if (company && company.value === "all") {
      setAssociateCompanies([ALL_OPTION]);
      setAssociateCompany(ALL_OPTION);
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
    } else if (company && company.company) {
      const assocCompanies = [
        ALL_OPTION,
        ...(company.company.associate_companies || []).map(function (ac: any) {
          return {
            value: ac.id,
            label: ac.name,
            associate_company: ac,
            locations: ac.locations || [],
          };
        }),
      ];
      setAssociateCompanies(assocCompanies);
      setAssociateCompany(ALL_OPTION);
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
    } else {
      setAssociateCompanies([]);
      setAssociateCompany(null);
      setLocations([]);
      setLocation(null);
    }
  }, [company]);

  useEffect(() => {
    if (associateCompany && associateCompany.value === "all") {
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
    } else if (associateCompany && associateCompany.locations) {
      const locationOptions = [
        ALL_OPTION,
        ...associateCompany.locations.map(function (loc: any) {
          const label =
            loc.city && loc.city.name
              ? loc.name + ", " + loc.city.name
              : loc.name;
          return { value: loc.id, label: label, location: loc };
        }),
      ];
      setLocations(locationOptions);
      setLocation(ALL_OPTION);
    }
  }, [associateCompany]);

  useEffect(() => {
    const filters: any = {};

    if (company && company.value === "all") {
      const allCompanyIds = userCompanies.map((c: any) => c.id);
      if (allCompanyIds.length > 0) {
        filters.company_id_in = allCompanyIds;
      }
    } else if (company && company.value !== "all") {
      filters.company_id = company.value;
    }

    if (associateCompany && associateCompany.value !== "all") {
      filters.associate_company_id = associateCompany.value;
    }

    if (location && location.value !== "all") {
      filters.location_id = location.value;
    }

    onChange(filters);
  }, [company, associateCompany, location]);


  return (
    <>
      <div className="px-2">
        <label className="filter-label">
          <small>Establishment Type</small>
        </label>
        <Select
          placeholder="Establishment Type"
          options={ESTABLISHMENT_TYPES}
          onChange={(option: any) => onEstablishmentTypeChange(option.value)}
          value={ESTABLISHMENT_TYPES.find(opt => opt.value === establishmentType)}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Company</small>
        </label>
        <Select
          placeholder="Company"
          options={companies}
          onChange={setCompany}
          value={company}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
          isLoading={isFetching}
          isDisabled={!establishmentType}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Associate Company</small>
        </label>
        <Select
          placeholder="Associate Company"
          options={associateCompanies}
          onChange={setAssociateCompany}
          value={associateCompany}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
          isDisabled={!company || company.value === "all"}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Location</small>
        </label>
        <Select
          placeholder="Location"
          options={locations}
          onChange={setLocation}
          value={location}
          className="select-control"
          components={{ DropdownIndicator: PerformerDropdownIndicator }}
          isDisabled={!associateCompany || associateCompany.value === "all"}
        />
      </div>
    </>
  );
}

export default PerformerFilters;
